import React, {useState, useEffect} from 'react';
import {CreateMarketerRequest} from '../models/CreateMarketer';
import {FormField, FormSection, FormActions} from '../../../Presentation/components/common';
import {useFormState, CommonValidationRules, createFieldProps} from '../../../Presentation/utils/formUtils';
import {useMarketersStore} from '../store/marketersStore';

interface AddMarketerFormProps {
  onSuccess?: (marketer: any) => void;
  loading?: boolean;
  onCancel?: () => void;
}

const AddMarketerForm: React.FC<AddMarketerFormProps> = ({
  onSuccess,
  loading = false,
  onCancel
}) => {
  // Local state for password visibility
  const [showPassword, setShowPassword] = useState(false);

  // Get createMarketer function from store
  const {createMarketer} = useMarketersStore();

  // Initialize form state with centralized form utilities
  const [formState, formActions] = useFormState(
    {
      username: '',
      fullName: '',
      email: '',
      phoneNumber: '',
      password: ''
    },
    {
      username: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'اسم المستخدم مطلوب';
          }
          if (value.length < 3) {
            return 'اسم المستخدم يجب أن يكون 3 أحرف على الأقل';
          }
          if (!/^[a-zA-Z0-9_]+$/.test(value)) {
            return 'اسم المستخدم يجب أن يحتوي على أحرف وأرقام و _ فقط';
          }
          return null;
        }
      },
      fullName: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'الاسم الكامل مطلوب';
          }
          if (value.length < 2) {
            return 'الاسم الكامل يجب أن يكون حرفين على الأقل';
          }
          return null;
        }
      },
      email: CommonValidationRules.email,
      phoneNumber: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'رقم الهاتف مطلوب';
          }
          const phoneRegex = /^(\+966|966|0)?5[0-9]{8}$/;
          if (!phoneRegex.test(value.replace(/\s+/g, ''))) {
            return 'صيغة رقم الهاتف غير صحيحة (يجب أن يكون رقم سعودي)';
          }
          return null;
        }
      },
      password: {
        required: true,
        customValidator: (value: string) => {
          if (!value?.trim()) {
            return 'كلمة المرور مطلوبة';
          }
          if (value.length < 8) {
            return 'كلمة المرور يجب أن تكون 8 أحرف على الأقل';
          }
          if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
            return 'كلمة المرور يجب أن تحتوي على حرف صغير وكبير ورقم';
          }
          return null;
        }
      }
    }
  );


  const handleSubmit = async (data: any) => {
    try {
      // Clear any existing errors first
      formActions.clearErrors();

      // Clean and prepare data in camelCase (as CreateMarketerRequest expects)
      const cleanedData: CreateMarketerRequest = {
        username: data.username?.toLowerCase().trim() || '',
        fullName: data.fullName?.trim() || '',
        email: data.email?.toLowerCase().trim() || '',
        phoneNumber: data.phoneNumber?.replace(/\s+/g, '') || '',
        password: data.password || ''
      };

      // Send to store/useCase/repository (they expect camelCase)
      const newMarketer = await createMarketer(cleanedData);

      onSuccess?.(newMarketer);
    } catch (error) {
      // Error notification is handled by StandardizedErrorHandler in the store
    }
  };

  const generateUsername = () => {
    // Generate simple username with random number
    const randomNum = Math.floor(Math.random() * 9999) + 1000; // 4-digit number
    const username = `marketer_${randomNum}`;

    formActions.setField('username', username);
  };

  const generatePassword = () => {
    // Generate simple but strong password
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let password = '';

    // Start with a capital letter
    password += 'M'; // For Marketer

    // Add random characters
    for (let i = 1; i < 10; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }

    // Add a number at the end
    password += Math.floor(Math.random() * 99) + 10;

    formActions.setField('password', password);
  };

  return (
    <div>
      <form onSubmit={(e) => { e.preventDefault(); formActions.submit(handleSubmit); }} className="space-y-4">
          <FormSection title="بيانات المسوق الأساسية">
            {/* Username */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                اسم المستخدم *
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <FormField
                    type="text"
                    placeholder="marketer_ahmed_123"
                    {...createFieldProps('username', formState, formActions)}
                    disabled={loading}
                  />
                </div>
                <button
                  type="button"
                  onClick={generateUsername}
                  className="px-3 py-2 text-sm bg-brand-500 text-white rounded-md hover:bg-brand-600"
                  title="توليد اسم مستخدم عشوائي"
                >
                  توليد
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                يُستخدم لتسجيل الدخول. انقر "توليد" لإنشاء اسم مستخدم تلقائياً
              </p>
            </div>

            <FormField
              type="text"
              label="الاسم الكامل *"
              placeholder="أحمد محمد المسوق"
              {...createFieldProps('fullName', formState, formActions)}
              disabled={loading}
            />

            <FormField
              type="email"
              label="البريد الإلكتروني *"
              placeholder="ahmed.marketer@company.com"
              {...createFieldProps('email', formState, formActions)}
              disabled={loading}
            />

            <div>
              <FormField
                type="tel"
                label="رقم الهاتف *"
                placeholder="+966501234567"
                {...createFieldProps('phoneNumber', formState, formActions)}
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                يجب أن يكون رقم هاتف سعودي صحيح
              </p>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                كلمة المرور *
              </label>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <FormField
                    type={showPassword ? 'text' : 'password'}
                    placeholder="SecurePassword123"
                    {...createFieldProps('password', formState, formActions)}
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
                <button
                  type="button"
                  onClick={generatePassword}
                  className="px-3 py-2 text-sm bg-green-500 text-white rounded-md hover:bg-green-600"
                  title="توليد كلمة مرور قوية"
                >
                 توليد
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                8 أحرف على الأقل. انقر "توليد" لإنشاء كلمة مرور قوية تلقائياً
              </p>
            </div>
          </FormSection>


          <FormActions
            onSubmit={() => formActions.submit(handleSubmit)}
            onCancel={onCancel}
            isLoading={loading || formState.isSubmitting}
            disabled={!formState.isValid}
            submitLabel="إضافة المسوق"
            cancelLabel="إلغاء"
          />
      </form>

      {/* Guidelines */}
      <div className="mt-6 p-4 bg-brand-50 border border-brand-200 rounded-lg">
        <h4 className="text-sm font-semibold text-brand-900 mb-2">إرشادات مهمة:</h4>
        <ul className="text-xs text-brand-800 space-y-1">
          <li>• تأكد من صحة البريد الإلكتروني ورقم الهاتف</li>
          <li>• اسم المستخدم يجب أن يكون فريداً في النظام</li>
          <li>• كلمة المرور يجب أن تكون قوية وآمنة</li>
          <li>• سيتم تعيين دور "مسوق" تلقائياً للمستخدم الجديد</li>
        </ul>
      </div>
    </div>
  );
};

export default AddMarketerForm;
